import React, { useState } from "react";
import ReactPDF, { Text, View } from "@react-pdf/renderer";
import { styles } from "../styles/Slide8Styles";
import ResultsNavigationBar from "../components/ResultsNavigationBar";
import { black, blue, darkGreen, red } from "../styles/PDFColors";
import {
  CallOption,
  CallOptions,
  CliffPeriod,
  TimeType,
  VestingFrequency,
} from "../ResultContext";
import Canvas = ReactPDF.Canvas;
import { BackLoaded } from "../constants/BackLoaded";

function StringBuilder(callOptions: CallOptions) {
  let resultString = "";

  if (!callOptions.callOptions) {
    return "No call options on";
  }

  if (callOptions.shareValue === CallOption.CustomPrice) {
    resultString += `Custom price (€${callOptions.shareValuePrice}) on`;
  }

  if (callOptions.shareValue === CallOption.FairMarket) {
    resultString += `Fair market value on`;
  }

  if (callOptions.shareValue === CallOption.FairMarketDiscount) {
    resultString += `Fair market value with a discount of ${callOptions.shareValueDiscount}% on`;
  }

  if (callOptions.shareValue === CallOption.Euro) {
    resultString += "1 symbolic euro for";
  }

  if (callOptions.shareValue === CallOption.InitialSubscription) {
    resultString += "Initial subscription price on";
  }

  return resultString;
}

export function Slide8({
  vestingDurationResult,
  vestingCliffResult,
  vestingFrequencyResult,
  callOptionsGoodResult,
  callOptionsEarlyResult,
  callOptionsBadResult,
}: {
  vestingDurationResult: {
    time: number;
    type: TimeType;
  };
  vestingCliffResult: CliffPeriod;
  vestingFrequencyResult: {
    frequency: VestingFrequency;
    type: string;
  };
  callOptionsGoodResult: {
    vested: CallOptions;
    unvested: CallOptions;
    vestedBad: CallOptions;
  };
  callOptionsEarlyResult: {
    vested: CallOptions;
    unvested: CallOptions;
    vestedBad: CallOptions;
  };
  callOptionsBadResult: {
    vested: CallOptions;
    unvested: CallOptions;
    vestedBad: CallOptions;
  };
}) {
  const [percentageExample, setPercentageExample] = useState<number>(0);

  if (
    !vestingDurationResult ||
    !vestingCliffResult ||
    !callOptionsGoodResult ||
    !callOptionsEarlyResult ||
    !callOptionsBadResult
  ) {
    return <Text>Not all information is filled in for this section</Text>;
  }

  function PercentageCalculatorYears(years: number, index: number) {
    if (vestingFrequencyResult.type === "Linear") {
      const percentage = (100 / years) * index;
      return `${
        percentage.toString().includes(".") ? percentage.toFixed(2) : percentage
      }%`;
    } else {
      return `${Object.values(BackLoaded[vestingDurationResult.time])
        .slice(0, index)
        .reduce((a, b) => {
          return a + b;
        }, 0)}%`;
    }
  }

  function PercentageBackloaded(years: number, index: number) {
    const valuesBackloaded = Object.values(
      BackLoaded[vestingDurationResult.time]
    );
    const backloadedThisYear = valuesBackloaded
      .slice(0, index)
      .reduce((a, b) => {
        return a + b;
      }, 0);
    const backloadedNextYear = valuesBackloaded
      .slice(0, index + 1)
      .reduce((a, b) => {
        return a + b;
      }, 0);

    return backloadedNextYear - backloadedThisYear;
  }

  function PercentageCalculatorExamples(
    years: number,
    index: number,
    startX: number,
    distanceBetweenYears: number,
    cliffMonths: number,
    lengthVerticalMonth: number
  ) {
    if (vestingFrequencyResult.type === "Linear") {
      const percentage = (100 / years) * index + 100 / years / 2;
      return `${
        startX + distanceBetweenYears / 2 + distanceBetweenYears * index >=
        startX + cliffMonths * lengthVerticalMonth
          ? percentage.toString().includes(".")
            ? percentage.toFixed(2)
            : percentage
          : "0"
      }%`;
    } else {
      const backLoadedPrevious = Object.values(
        BackLoaded[vestingDurationResult.time]
      )
        .slice(0, index)
        .reduce((a, b) => {
          return a + b;
        }, 0);

      const backLoadedYear = Object.values(
        BackLoaded[vestingDurationResult.time]
      )
        .slice(0, index + 1)
        .reduce((a, b) => {
          return a + b;
        }, 0);
      const percentage =
        backLoadedPrevious + (backLoadedYear - backLoadedPrevious) / 2;
      return `${
        startX + distanceBetweenYears / 2 + distanceBetweenYears * index >=
        startX + cliffMonths * lengthVerticalMonth
          ? percentage.toString().includes(".")
            ? percentage.toFixed(2)
            : percentage
          : "0"
      }%`;
    }
  }

  return (
    <View style={styles.backgroundBlue}>
      <ResultsNavigationBar selectIdx={5} />
      <View style={styles.contentContainer}>
        <View style={styles.titleContainer}>
          <View style={styles.titleContainerLeft}>
            <Text style={styles.textTitleLeft}>Example</Text>
          </View>
          <View style={styles.titleContainerRight}>
            <Text style={styles.textTitleRight}>
              {vestingFrequencyResult.type === "Linear"
                ? "Linear"
                : "Backloaded"}{" "}
              vesting schedule of {vestingDurationResult.time} years{" "}
              {vestingCliffResult.cliffOptions
                ? `with a cliff of ${vestingCliffResult.cliffShares} months `
                : ``}
              and {vestingFrequencyResult.frequency.toLowerCase()} vesting
              thereafter.
            </Text>
          </View>
        </View>
        <View style={styles.timelineContainer}>
          <Canvas
            paint={(painterObject) => {
              // main line coordinates
              const baseHeight = 100;
              const startX = 15;
              const endX = 760;
              const lengthBase = endX - startX;

              // input variables and derivatives
              const cliffMonths = vestingCliffResult.cliffOptions
                ? parseFloat(vestingCliffResult.cliffShares)
                : 0;
              const vestingYears: number = vestingDurationResult.time;
              const vestingMonths = vestingYears * 12;
              const vestingFrequency = vestingFrequencyResult.frequency;

              const vestingType = vestingFrequencyResult.type;

              const vestingFrequencyMonths: number =
                vestingFrequency === VestingFrequency.Monthly
                  ? 1
                  : vestingFrequency === VestingFrequency.Quarterly
                  ? 3
                  : vestingFrequency === VestingFrequency.SixMonths
                  ? 6
                  : 12;

              const vestingFrequencyFractions =
                vestingFrequency === VestingFrequency.Monthly
                  ? 12
                  : vestingFrequency === VestingFrequency.Quarterly
                  ? 4
                  : vestingFrequency === VestingFrequency.SixMonths
                  ? 2
                  : 1;

              // vertical year lines
              const distanceBetweenYears = lengthBase / vestingYears;
              const lengthVerticalYears = 30;

              // vesting frequency vertical lines
              const distanceBetweenLines =
                distanceBetweenYears / vestingFrequencyFractions;
              const lengthVesting = 15;
              const startVestingX = startX;
              const lengthVerticalMonth = lengthBase / vestingMonths;

              // text above vesting accolade
              const vestingPeriodMonths =
                cliffMonths > vestingFrequencyMonths
                  ? vestingMonths - cliffMonths
                  : vestingMonths;

              const vestingPeriod =
                vestingFrequency === VestingFrequency.Monthly
                  ? vestingPeriodMonths
                  : vestingFrequency === VestingFrequency.Quarterly
                  ? Math.floor(vestingPeriodMonths / 3)
                  : vestingFrequency === VestingFrequency.SixMonths
                  ? vestingPeriodMonths / 6
                  : vestingPeriodMonths / 12;

              const vestingFrequencyText =
                vestingFrequency === VestingFrequency.Monthly
                  ? "months"
                  : vestingFrequency === VestingFrequency.Quarterly
                  ? "quarters"
                  : vestingFrequency === VestingFrequency.SixMonths
                  ? "six months"
                  : "years";

              // line at end of cliff period
              const lengthVerticalCliff = 20;

              // draw main line
              painterObject
                .save()
                .moveTo(startX, baseHeight)
                .lineTo(endX, baseHeight)
                .stroke();

              // draw vertical year lines and add text
              for (let i = 1; i < vestingYears + 1; i++) {
                painterObject
                  .fontSize(17)
                  .save()
                  .moveTo(
                    startX + i * distanceBetweenYears,
                    baseHeight - lengthVerticalYears / 2
                  )
                  .lineTo(
                    startX + i * distanceBetweenYears,
                    baseHeight + lengthVerticalYears / 2
                  )
                  .text(
                    `${i}`,
                    startX + i * distanceBetweenYears - 4,
                    baseHeight + lengthVerticalYears / 2 + 5
                  )
                  .strokeColor(black)
                  .stroke();

                if (
                  startX + i * distanceBetweenYears >
                  (cliffMonths * distanceBetweenYears) / 12
                ) {
                  // arrows
                  painterObject
                    .save()
                    .moveTo(
                      startX + i * distanceBetweenYears,
                      baseHeight + lengthVerticalYears / 2 + 20
                    )
                    .lineTo(
                      startX + i * distanceBetweenYears,
                      baseHeight + lengthVerticalYears / 2 + 40
                    )
                    .lineTo(
                      startX + i * distanceBetweenYears - 2,
                      baseHeight + lengthVerticalYears / 2 + 40
                    )
                    .lineTo(
                      startX + i * distanceBetweenYears,
                      baseHeight + lengthVerticalYears / 2 + 43
                    )
                    .lineTo(
                      startX + i * distanceBetweenYears + 2,
                      baseHeight + lengthVerticalYears / 2 + 40
                    )
                    .lineTo(
                      startX + i * distanceBetweenYears,
                      baseHeight + lengthVerticalYears / 2 + 40
                    )
                    .strokeColor(blue)
                    .stroke();

                  // text under arrows
                  painterObject
                    .fontSize(8)
                    .save()
                    .text(
                      PercentageCalculatorYears(vestingYears, i),
                      startX + i * distanceBetweenYears - 6,
                      baseHeight + lengthVerticalYears / 2 + 48
                    )
                    .text(
                      `Vested`,
                      startX + i * distanceBetweenYears - 10,
                      baseHeight + lengthVerticalYears / 2 + 56
                    )
                    .strokeColor(black)
                    .stroke();
                }
              }

              // draw vertical vesting lines
              if (vestingFrequency !== "Yearly") {
                for (
                  let i = 1;
                  startVestingX + i * distanceBetweenLines < endX;
                  i++
                ) {
                  if (
                    startVestingX + i * distanceBetweenLines >
                    startX + cliffMonths * lengthVerticalMonth
                  ) {
                    painterObject
                      .fontSize(8)
                      .save()
                      .moveTo(
                        startVestingX + i * distanceBetweenLines,
                        baseHeight - lengthVesting / 2
                      )
                      .lineTo(
                        startVestingX + i * distanceBetweenLines,
                        baseHeight + lengthVesting / 2
                      )
                      .strokeColor(black)
                      .stroke();
                  }
                }
              }

              // add accolade above vesting period
              painterObject
                .save()
                .moveTo(
                  cliffMonths < vestingFrequencyMonths
                    ? startX
                    : startX + cliffMonths * lengthVerticalMonth,
                  baseHeight - lengthVesting / 2 - 25
                )
                .lineTo(
                  cliffMonths < vestingFrequencyMonths
                    ? startX
                    : startX + cliffMonths * lengthVerticalMonth,
                  baseHeight - lengthVesting / 2 - 40
                )
                .lineTo(endX, baseHeight - lengthVesting / 2 - 40)
                .lineTo(endX, baseHeight - lengthVesting / 2 - 25)
                .stroke();

              // if backloaded add more vertical stripes to accolade
              if (vestingFrequencyResult.type !== "Linear") {
                for (let i = 1; i < vestingYears + 1; i++) {
                  if (
                    startX + i * distanceBetweenYears >
                    startX + cliffMonths * lengthVerticalMonth
                  ) {
                    painterObject
                      .save()
                      .moveTo(
                        startX + i * distanceBetweenYears,
                        baseHeight - lengthVesting / 2 - 25
                      )
                      .lineTo(
                        startX + i * distanceBetweenYears,
                        baseHeight - lengthVesting / 2 - 40
                      )
                      .strokeColor(black)
                      .stroke();
                  }

                  // add text
                  if (i < vestingYears) {
                    painterObject
                      .fontSize(8)
                      .save()
                      .text(
                        `${vestingFrequency} ${(
                          PercentageBackloaded(vestingYears, i) /
                          vestingFrequencyFractions
                        ).toFixed(2)}%`,
                        startX + i * distanceBetweenYears,
                        baseHeight - lengthVesting / 2 - 70
                      )
                      .text(
                        `(=${PercentageBackloaded(
                          vestingYears,
                          i
                        )}%/${vestingFrequencyFractions}`,
                        startX + i * distanceBetweenYears,
                        baseHeight - lengthVesting / 2 - 60
                      )
                      .text(
                        `${vestingFrequencyText})`,
                        startX + i * distanceBetweenYears,
                        baseHeight - lengthVesting / 2 - 50
                      )
                      .strokeColor(black)
                      .stroke();
                  }
                }
              }

              // add text on top of accolade about the vesting frequency (in the linear case)
              if (vestingFrequencyResult.type === "Linear") {
                painterObject
                  .save()
                  .text(
                    `${vestingFrequency} ${(
                      100 /
                      (vestingFrequencyFractions * vestingYears)
                    ).toFixed(2)}% (=${(
                      100 -
                      (cliffMonths * 100) / vestingMonths
                    ).toFixed(2)}% /${vestingPeriod.toFixed(
                      2
                    )} ${vestingFrequencyText})`,
                    startVestingX +
                      (Math.round(
                        (startX +
                          cliffMonths * lengthVerticalMonth -
                          startVestingX) /
                          distanceBetweenLines
                      ) *
                        distanceBetweenLines +
                        endX) /
                        2 -
                      100,
                    baseHeight - lengthVesting / 2 - 50
                  );
              }

              // if cliff is relevant compared to vesting frequency months
              if (cliffMonths >= vestingFrequencyMonths) {
                // draw line at end of cliff
                painterObject
                  .save()
                  .moveTo(
                    startX + cliffMonths * lengthVerticalMonth,
                    baseHeight - lengthVerticalCliff / 2
                  )
                  .lineTo(
                    startX + cliffMonths * lengthVerticalMonth,
                    baseHeight + lengthVerticalCliff / 2
                  )
                  .strokeColor(black)
                  .stroke();

                // draw arrow from start to end of cliff
                painterObject
                  .save()
                  .moveTo(startX, baseHeight - lengthVerticalYears / 2 - 5)
                  .quadraticCurveTo(
                    startX + (cliffMonths * lengthVerticalMonth) / 2,
                    baseHeight - lengthVerticalYears / 2 - 35,
                    startX + cliffMonths * lengthVerticalMonth - 1,
                    baseHeight - lengthVerticalYears / 2 - 5
                  )
                  .moveTo(
                    startX + cliffMonths * lengthVerticalMonth - 4,
                    baseHeight - lengthVerticalYears / 2 - 10
                  )
                  .lineTo(
                    startX + cliffMonths * lengthVerticalMonth - 1,
                    baseHeight - lengthVerticalYears / 2 - 5
                  )
                  .lineTo(
                    startX + cliffMonths * lengthVerticalMonth - 6,
                    baseHeight - lengthVerticalYears / 2 - 4
                  )
                  .strokeColor(blue)
                  .stroke();

                // write text over cliff arrow
                painterObject
                  .save()
                  .text(
                    `${cliffMonths} month(s) cliff`,
                    startX + (cliffMonths * lengthVerticalMonth) / 2 - 25,
                    baseHeight - lengthVerticalYears / 2 - 42
                  );
              }

              // draw small arrows between vesting periods
              let firstI = 0;
              for (
                let i = 1;
                startVestingX + (i - 1) * distanceBetweenLines < endX;
                i++
              ) {
                if (
                  startVestingX + (i - 1) * distanceBetweenLines >
                  startX + cliffMonths * lengthVerticalMonth
                ) {
                  if (firstI === 0) {
                    firstI = i;
                  }
                  painterObject
                    .save()
                    .moveTo(
                      startVestingX + (i - 1) * distanceBetweenLines + 1,
                      baseHeight - lengthVerticalYears / 2 - 5
                    )
                    .quadraticCurveTo(
                      startVestingX +
                        (i - 1) * distanceBetweenLines +
                        distanceBetweenLines / 2,
                      baseHeight - lengthVerticalYears / 2 - 20,
                      startVestingX + i * distanceBetweenLines - 1,
                      baseHeight - lengthVerticalYears / 2 - 5
                    )
                    .lineTo(
                      startVestingX + i * distanceBetweenLines - 3,
                      baseHeight - lengthVerticalYears / 2 - 7
                    )
                    .moveTo(
                      startVestingX + i * distanceBetweenLines - 1,
                      baseHeight - lengthVerticalYears / 2 - 5
                    )
                    .lineTo(
                      startVestingX + i * distanceBetweenLines - 1,
                      baseHeight - lengthVerticalYears / 2 - 8
                    )
                    .strokeColor(blue)
                    .stroke();
                }
              }

              // draw arrow from end of cliff (or start of line if cliff irrelevant) to first vesting timestamp
              const i = firstI - 1;
              if (firstI > 0) {
                painterObject
                  .save()
                  .moveTo(
                    cliffMonths < vestingFrequencyMonths
                      ? startX
                      : startX + cliffMonths * lengthVerticalMonth,
                    baseHeight - lengthVerticalYears / 2 - 5
                  )
                  .quadraticCurveTo(
                    startVestingX +
                      (i - 1) * distanceBetweenLines +
                      distanceBetweenLines / 2,
                    baseHeight - lengthVerticalYears / 2 - 20,
                    startVestingX + i * distanceBetweenLines - 1,
                    baseHeight - lengthVerticalYears / 2 - 5
                  )
                  .lineTo(
                    startVestingX + i * distanceBetweenLines - 3,
                    baseHeight - lengthVerticalYears / 2 - 7
                  )
                  .moveTo(
                    startVestingX + i * distanceBetweenLines - 1,
                    baseHeight - lengthVerticalYears / 2 - 5
                  )
                  .lineTo(
                    startVestingX + i * distanceBetweenLines - 1,
                    baseHeight - lengthVerticalYears / 2 - 8
                  )
                  .strokeColor(blue)
                  .stroke();
              } else {
                painterObject
                  .save()
                  .moveTo(
                    cliffMonths < vestingFrequencyMonths
                      ? startX
                      : startX + cliffMonths * lengthVerticalMonth,
                    baseHeight - lengthVerticalYears / 2 - 5
                  )
                  .quadraticCurveTo(
                    cliffMonths < vestingFrequencyMonths
                      ? startX
                      : startX +
                          lengthBase -
                          (lengthBase - cliffMonths * lengthVerticalMonth) / 2,
                    baseHeight - lengthVerticalYears / 2 - 20,
                    endX - 1,
                    baseHeight - lengthVerticalYears / 2 - 5
                  )
                  .lineTo(endX - 3, baseHeight - lengthVerticalYears / 2 - 7)
                  .moveTo(endX - 1, baseHeight - lengthVerticalYears / 2 - 5)
                  .lineTo(endX - 1, baseHeight - lengthVerticalYears / 2 - 8)
                  .strokeColor(blue)
                  .stroke();
              }

              // add red crosses with corresponding arrows and text
              for (let i = 0; i < vestingYears; i++) {
                // crosses
                painterObject
                  .save()
                  .strokeColor(red)
                  .moveTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i -
                      2,
                    baseHeight - 3
                  )
                  .lineTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i +
                      2,
                    baseHeight + 3
                  )
                  .moveTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i +
                      2,
                    baseHeight - 3
                  )
                  .lineTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i -
                      2,
                    baseHeight + 3
                  )
                  .stroke();

                // arrow
                painterObject
                  .save()
                  .strokeColor(blue)
                  .moveTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i,
                    baseHeight + lengthVerticalYears / 2
                  )
                  .lineTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i,
                    baseHeight + lengthVerticalYears / 2 + 20
                  )
                  .lineTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i -
                      2,
                    baseHeight + lengthVerticalYears / 2 + 20
                  )
                  .lineTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i,
                    baseHeight + lengthVerticalYears / 2 + 23
                  )
                  .lineTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i +
                      2,
                    baseHeight + lengthVerticalYears / 2 + 20
                  )
                  .lineTo(
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i,
                    baseHeight + lengthVerticalYears / 2 + 20
                  )
                  .stroke();

                // text
                painterObject
                  .save()
                  .text(
                    PercentageCalculatorExamples(
                      vestingYears,
                      i,
                      startX,
                      distanceBetweenYears,
                      cliffMonths,
                      lengthVerticalMonth
                    ),
                    startX +
                      distanceBetweenYears / 2 +
                      distanceBetweenYears * i -
                      5,
                    baseHeight + lengthVerticalYears / 2 + 26
                  )
                  .stroke();
              }

              // add green circle
              painterObject
                .save()
                .circle(
                  cliffMonths < vestingFrequencyMonths
                    ? startX + lengthBase / 4
                    : startX +
                        cliffMonths * lengthVerticalMonth +
                        lengthBase / 4,
                  baseHeight,
                  5
                )
                .strokeColor(darkGreen)
                .fillColor(darkGreen)
                .fill()
                .stroke();

              setPercentageExample(
                cliffMonths < vestingFrequencyMonths
                  ? (lengthBase / 4 / lengthBase) * 100
                  : ((cliffMonths * lengthVerticalMonth + lengthBase / 4) /
                      lengthBase) *
                      100
              );
              return painterObject;
            }}
          />
        </View>
        <View style={styles.bottomContainer}>
          <View style={styles.bottomLeftContainer}>
            <View style={styles.textFounderContainer}>
              <View style={{ display: "flex", flexDirection: "row" }}>
                <Text style={styles.red}>X</Text>{" "}
                <Text style={{ fontSize: "12px" }}>
                  {" "}
                  = Founder leaving after x months
                </Text>
              </View>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "5px",
                }}
              >
                <View style={styles.green} />{" "}
                <Text style={{ fontSize: "12px" }}>
                  {" "}
                  = Example, see related information on the right
                </Text>
              </View>
            </View>
          </View>
          <View style={styles.bottomRightContainer}>
            <View style={styles.leaverInfoContainer}>
              <View style={styles.leaverTypeContainer}>
                <Text style={styles.textLeaverType}>Good Leaver</Text>
              </View>
              <View style={styles.callOptionsContainer}>
                <Text style={styles.textCallOptions}>
                  - {StringBuilder(callOptionsGoodResult.vested)} vested (=
                  {percentageExample.toFixed(2)}%) shares
                </Text>

                <Text style={styles.textCallOptions}>
                  - {StringBuilder(callOptionsGoodResult.unvested)} unvested ( =
                  {(100 - percentageExample).toFixed(2)}
                  %) shares
                </Text>
              </View>
            </View>
            <View style={styles.leaverInfoContainer}>
              <View style={styles.leaverTypeContainer}>
                <Text style={styles.textLeaverType}>Early Leaver</Text>
              </View>
              <View style={styles.callOptionsContainer}>
                <Text style={styles.textCallOptions}>
                  - {StringBuilder(callOptionsEarlyResult.vested)} vested (=
                  {percentageExample.toFixed(2)}%) shares
                </Text>
                <Text style={styles.textCallOptions}>
                  - {StringBuilder(callOptionsEarlyResult.unvested)} unvested (=
                  {(100 - percentageExample).toFixed(2)}
                  %) shares
                </Text>
              </View>
            </View>
            <View style={styles.leaverInfoContainer}>
              <View style={styles.leaverTypeContainer}>
                <Text style={styles.textLeaverType}>Bad Leaver</Text>
              </View>
              <View style={styles.callOptionsContainer}>
                <Text style={styles.textCallOptions}>
                  - {StringBuilder(callOptionsBadResult.vested)} vested (=
                  {percentageExample.toFixed(2)}%) shares
                </Text>
                <Text style={styles.textCallOptions}>
                  - {StringBuilder(callOptionsBadResult.unvested)} unvested (=
                  {(100 - percentageExample).toFixed(2)}
                  %) shares
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
